import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, DeleteOutline, WarningAmber } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { accountAPI } from '../services/api';
import { tokens } from '../theme';

const consequences = [
  'All personal data will be permanently deleted.',
  'Fasting history, trends, and streaks will be removed.',
  'Achievements and account preferences will be lost.',
  'Premium access will end immediately.',
  'This action cannot be undone.',
];

export default function DeleteAccountPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [otp, setOtp] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(false);

  const handleRequestOTP = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await accountAPI.requestDeletionOTP();
      if (response.data.success) {
        setMaskedEmail(response.data.data.email);
        setSuccess('A verification code was sent to your email.');
        setActiveStep(1);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send verification code.');
    } finally {
      setLoading(false);
      setConfirmDialog(false);
    }
  };

  const handleVerifyAndDelete = async () => {
    if (otp.length !== 6) {
      setError('Please enter the 6-digit verification code.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await accountAPI.verifyAndDelete(otp);
      if (response.data.success) {
        setSuccess('Your account has been deleted.');
        setActiveStep(2);
        setTimeout(async () => {
          await logout();
          navigate('/');
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: tokens.bg, py: { xs: 4, md: 8 } }}>
      <Container maxWidth="md">
        <Button startIcon={<ArrowBack />} onClick={() => navigate({ pathname: '/settings', search: location.search })} sx={{ mb: 4 }}>
          Back to settings
        </Button>

        <Card sx={{ p: { xs: 1, md: 2 } }}>
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h2" sx={{ color: tokens.cream, fontSize: { xs: '2.6rem', md: '3.4rem' }, mb: 1.5 }}>
                  Delete your account
                </Typography>
                <Typography sx={{ color: tokens.textSoft }}>
                  We’re sorry to see you go. Please review the impact carefully before continuing.
                </Typography>
              </Box>

              {(error || success) && (
                <Alert
                  severity={error ? 'error' : 'success'}
                  sx={{
                    bgcolor: error ? 'rgba(230,124,115,0.08)' : 'rgba(123,196,142,0.1)',
                    color: error ? '#f3b0aa' : '#c6efd0',
                  }}
                >
                  {error || success}
                </Alert>
              )}

              {activeStep === 0 && (
                <>
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 5,
                      border: `1px solid rgba(230,124,115,0.24)`,
                      bgcolor: 'rgba(230,124,115,0.06)',
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                      <WarningAmber sx={{ color: tokens.danger }} />
                      <Typography variant="h6" sx={{ color: tokens.cream }}>
                        What happens next
                      </Typography>
                    </Stack>
                    <List sx={{ p: 0 }}>
                      {consequences.map((item) => (
                        <ListItem key={item} sx={{ px: 0 }}>
                          <ListItemText
                            primary={item}
                            primaryTypographyProps={{ color: tokens.textSoft, fontWeight: 500 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button fullWidth variant="outlined" onClick={() => navigate({ pathname: '/settings', search: location.search })}>
                      Cancel
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      color="error"
                      startIcon={<DeleteOutline />}
                      onClick={() => setConfirmDialog(true)}
                      sx={{
                        bgcolor: tokens.danger,
                        color: '#fff',
                        '&:hover': { bgcolor: '#cf6d65' },
                      }}
                    >
                      Continue with deletion
                    </Button>
                  </Stack>
                </>
              )}

              {activeStep === 1 && (
                <Stack spacing={2.5}>
                  <Typography sx={{ color: tokens.textSoft }}>
                    Enter the code sent to <strong>{maskedEmail || user?.email}</strong>.
                  </Typography>
                  <TextField
                    fullWidth
                    label="Verification code"
                    value={otp}
                    onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button fullWidth variant="outlined" onClick={() => setActiveStep(0)}>
                      Back
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleVerifyAndDelete}
                      disabled={loading}
                      sx={{ bgcolor: tokens.danger, color: '#fff', '&:hover': { bgcolor: '#cf6d65' } }}
                    >
                      {loading ? 'Deleting account…' : 'Delete account permanently'}
                    </Button>
                  </Stack>
                </Stack>
              )}

              {activeStep === 2 && (
                <Typography sx={{ color: tokens.textSoft }}>
                  Your account has been deleted. You’ll be redirected shortly.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
          <DialogTitle>Confirm account deletion</DialogTitle>
          <DialogContent>
            <Typography sx={{ color: tokens.textSoft }}>
              We’ll send a verification code to your email before we delete your account.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
            <Button onClick={handleRequestOTP} disabled={loading} sx={{ color: tokens.danger }}>
              {loading ? 'Sending…' : 'Send verification code'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}
